import React, { useState, useEffect } from 'react';
import 'leaflet/dist/leaflet.css';
import { BrowserRouter, Routes, Route, useSearchParams } from 'react-router-dom';
import { CampaignHub } from './pages/CampaignHub'; // Your existing page
import VoterHub from './pages/VoterHub';      // The component we built
import { VoterDiscoveryModal } from './components/VoterDiscoveryModal';
import { LogOut, User, ArrowLeft } from 'lucide-react';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Dashboard } from './components/Dashboard';
import { MedicalDashboard } from './components/MedicalDashboard';
import { PoliticalDashboard } from './components/PoliticalDashboard';
import { EducationalDashboard } from './components/EducationalDashboard';
import { ChurchDashboard } from './components/ChurchDashboard';
import CampaignManagement from './components/CampaignManagement';
import { POS } from './components/POS';
import CampaignTemplateManager from './components/CampaignTemplateManager';
import CampaignViewer from './components/CampaignViewer';
import ManifestoManager from './components/ManifestoManager';
import QRCheckInPage from './components/QRCheckInPage';
import QrCheckinPage from './pages/QrCheckinPage';
import QRSuccessPage from './pages/QRSuccessPage';
import MultiMallQrCheckinPage from './pages/MultiMallQrCheckinPage';
import ProductManager from './components/ProductManager';
import { DiasporaManager } from './components/DiasporaManager';
import QRGeneration from './components/QRGeneration';
import Analytics from './components/Analytics';
import VisitorEngagementManager from './components/VisitorEngagementManager';
import PaymentManagement from './components/PaymentManagement';
import ReceivingPaymentModule from './components/ReceivingPaymentModule';
import PaymentReceiptMessaging from './components/PaymentReceiptMessaging';
import { useAuth } from './contexts/AuthContext';
import { CustomerInquiries } from './components/CustomerInquiries';
import { PublicChurchHub } from './components/PublicChurchHub';
import { PublicSchoolHub } from './components/PublicSchoolHub';
import { MemberLogin } from './components/MemberLogin';
import { PublicHubRouter } from './components/PublicHubRouter';
import { MediaDashboard } from './components/MediaDashboard';
import { FinanceDashboard } from './components/FinanceDashboard';
import { DevelopmentDashboard } from './components/DevelopmentDashboard';
import { SchoolRouter } from './components/SchoolRouter';
import { AgriDashboard } from './components/AgriDashboard';
import { NGODashboard } from './components/NGODashboard';
import { PublicGivingPage } from './components/PublicGivingPage';
import { VisitorWelcomePage } from './components/VisitorWelcomePage';
import { ScreenDisplay } from './components/ScreenDisplay';
import { PublicAgricHub } from './components/PublicAgricHub';
import { AgentDashboard } from './components/AgentDashboard';
import { ElectionCandidateModal } from './components/ElectionCandidateModal';
import { PublicLogisticsHub } from './components/PublicLogisticsHub';
import { DiocesanRouter } from './components/DiocesanRouter';
import { MediaPresentationController } from './components/MediaPresentationController';
import SettingsPage from './pages/SettingsPage';
import './index.css';

// Reusable Header Component
const Header = ({ onBack, title, user, onLogout, showUserMenu, setShowUserMenu }: any) => (
  <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b flex items-center justify-between px-6 z-50">
    <div className="flex items-center gap-4">
      <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full">
        <ArrowLeft className="w-5 h-5" />
      </button>
      <h1 className="text-xl font-bold">{title}</h1>
    </div>
    <div className="relative">
      <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center gap-2">
        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
          <User className="w-5 h-5 text-blue-600" />
        </div>
      </button>
      {showUserMenu && (
        <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg py-1">
          <button onClick={onLogout} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      )}
    </div>
  </header>
);

function AppContent() {

  // =========================================================================
  // 🛡️ ZERO-DISRUPTION NATIVE APP URL RESTORER (Domain & Tenant Aware)
  // =========================================================================
  const isAndroidWebView = typeof navigator !== 'undefined' && /; wv\)/.test(navigator.userAgent);
  const currentUrl = new URL(window.location.href);
  const urlShopId = currentUrl.searchParams.get('shop_id');
  
  // 🌐 Dynamic Domain Detection
  const hostname = window.location.hostname;
  const isSchoolDomain = hostname.includes('tenearedu') || hostname.includes('school');
  const isAgriDomain = hostname.includes('sbo2') || hostname.includes('agri'); // 🚜 Added Agri Domain detector
  const isLogisticsDomain = hostname.includes('teneargroups2026') || hostname.includes('trucking') || hostname.includes('logistics');
  const isDioceseDomain = hostname.includes('diocese') || 
                        hostname.includes('ackerp') || 
                        hostname.includes('acknairobidiocese') || // ⬅️ Detects your Cloudflare Pages domain globally
                        currentUrl.pathname.startsWith('/diocese');

  if (urlShopId) {
    // 1. If params exist, back them up instantly for future sessions
    localStorage.setItem('__native_shop_id', urlShopId);
    localStorage.setItem('__native_biz_cat', currentUrl.searchParams.get('business_category') || (isSchoolDomain ? 'education' : isAgriDomain ? 'agricultural' : isLogisticsDomain ? 'logistics': 'church'));
  } else if (isAndroidWebView || currentUrl.pathname === '/') {
    // 2. If Android stripped params OR we hit the base root, resolve by hostname context

    // 🏛️ STEP 1: Check if the user is connecting through the global Diocesan domain
    const isDioceseDomain = hostname.includes('diocese') || hostname.includes('ackerp') || hostname.includes('acknairobidiocese');

    if (isDioceseDomain) {
      // Rewrite parameters specifically to target your high-level church gatekeeper
      currentUrl.pathname = '/diocese';
      currentUrl.searchParams.set('business_category', 'diocesan-erp');
      currentUrl.searchParams.delete('shop_id'); // Keeps the hierarchy clean of flat shop IDs

    } else if (isSchoolDomain) {

  
      // 🎓 School Hub Fallbacks
      const cachedShopId = localStorage.getItem('__native_shop_id') || 'YOUR_SCHOOL_DEFAULT_SHOP_ID'; // <-- Put your school shop ID string here
      const cachedBizCat = localStorage.getItem('__native_biz_cat') || 'education';

      currentUrl.pathname = '/school';
      currentUrl.searchParams.set('business_category', cachedBizCat);
      currentUrl.searchParams.set('shop_id', cachedShopId);

    } else if (isAgriDomain || currentUrl.pathname === '/agri') {
      // 🚜 Agricultural Hub Fallbacks (New Intercept Entry)
      const cachedShopId = localStorage.getItem('__native_shop_id') || '81'; // Default to Nyawara Ranch
      const cachedBizCat = localStorage.getItem('__native_biz_cat') || 'agricultural';
      currentUrl.pathname = '/agri';
      currentUrl.searchParams.set('business_category', cachedBizCat);
      currentUrl.searchParams.set('shop_id', cachedShopId);

    } else if (isLogisticsDomain || currentUrl.pathname === '/logistics') {
      // 🚚 Commercial Transport Hub Fallbacks (New Intercept Entry)
      const cachedShopId = localStorage.getItem('__native_shop_id') || '90'; // Default Logistics Shop ID
      const cachedBizCat = localStorage.getItem('__native_biz_cat') || 'logistics';
      currentUrl.pathname = '/logistics';
      currentUrl.searchParams.set('business_category', cachedBizCat);
      currentUrl.searchParams.set('shop_id', cachedShopId); 

    } else {
      // ⛪ Church Hub Fallbacks (Your original logic)
      const cachedShopId = localStorage.getItem('__native_shop_id') || '68'; 
      const cachedBizCat = localStorage.getItem('__native_biz_cat') || 'church';

      currentUrl.pathname = '/church';
      currentUrl.searchParams.set('business_category', cachedBizCat);
      currentUrl.searchParams.set('shop_id', cachedShopId);
    }

    // Force an instant clean reload before React renders anything else
    window.location.replace(currentUrl.toString());
    return null;
  }

  const { user, logout } = useAuth();
  const [currentView, setCurrentView] = useState('dashboard');
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams(); // Add this hook

  const bizCategory = searchParams.get('business_category');
  const shopId = searchParams.get('shop_id');
  const [isDiscoveryModalOpen, setIsDiscoveryModalOpen] = useState(false);
  const isChurchPath = window.location.pathname.includes('/church');
  const isSchoolPath = window.location.pathname.includes('/school');
  const isAgriPath = window.location.pathname.includes('/agri');
  const isLogisticsPath = window.location.pathname.includes('/logistics') || window.location.pathname.startsWith('/truck'); // 🚚 Added for Logistics
  const isDiocesePath = window.location.pathname.includes('/diocese') || bizCategory === 'diocesan-erp';

  const handleSelectCandidate = (shopId: string) => {
    // Update the URL parameters without a page reload
    // We keep 'business_category=political' to ensure the Hub stays active
    setSearchParams({
      business_category: 'political',
      shop_id: shopId
    });

    setIsDiscoveryModalOpen(false);
  };

  //  =========================================================================
  // 🔀 HIGH-PRIORITY REAL-TIME DISPLAY INTERCEPTOR
  // =========================================================================
  if (window.location.pathname === '/screen') {
    return <ScreenDisplay />;
  }

  if (window.location.pathname === '/broadcast-console') {
    return <MediaPresentationController />;
  }

  // =========================================================================
  // 🗳️ 2027 ELECTION NIGHT NATIVE MOBILE AGENT PORTAL INTERCEPT
  // =========================================================================
  if (window.location.pathname === '/agent-dashboard') {
    return <AgentDashboard />; 
  }

  // =========================================================================
  // 📱 DEDICATED HIGH-PRIORITY CANDIDATE MOBILE VIEW ROUTE INTERCEPT
  // =========================================================================
  if (window.location.pathname === '/candidate') {
    return <ElectionCandidateModal />;
  }


  // 2. VISITOR REDIRECT LOGIC (Before Auth/Protected Routes)
  // If a voter arrives with the political category, show them the campaign hub
  if (bizCategory?.startsWith('politic')) {
    return (
      <>
        <CampaignHub /> 
        <VoterDiscoveryModal 
          isOpen={!shopId || isDiscoveryModalOpen} // Open if no candidate selected
          onClose={() => setIsDiscoveryModalOpen(false)}
          onSelectCandidate={(id) => setSearchParams({ business_category: 'political', shop_id: id })}
        />
      </>
    );
  }


  <Route path="/hub/:id" element={<PublicHubRouter />} />


  // 2b. CHURCH VISITOR REDIRECT LOGIC (Public Access)
  // If a user arrives with the church category, show them the public hub
  if ((bizCategory === 'church' || isChurchPath) && !user) {

    const view = searchParams.get('view'); // Add this to detect the sub-page

    return (
      <div className="min-h-screen bg-gray-50">

        {/* If view=give is in URL, show the Giving Page, otherwise show Hub */}
        {view === 'give' ? (
          <PublicGivingPage /> 

        ) : view === 'welcome' ? (
          /* You can call a new VisitorWelcome component here */
          <VisitorWelcomePage shopId={Number(shopId) || 68} />
        ) : (

          <PublicChurchHub shopId={Number(shopId) || 68} />
        )}
      </div>
    );
  }

  // 🎓 DYNAMIC MULTI-ROLE EDUCATIONAL HUB GATEKEEPER
  if (bizCategory === 'educational' || isSchoolPath) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* 🌟 Swap PublicSchoolHub for SchoolRouter to support Director, Principal & Security panels */}
        <SchoolRouter 
          shopId={Number(shopId) || 69} 
          user={user} 
          onLogout={logout} 
        /> 
      </div>
    );
  }
        

  // 🚜 FARMER PUBLIC MOBILE HUB GATEKEEPER (New Intercept Injection)
  const isExplicitFarmPath = window.location.pathname.startsWith('/farm');

  if (bizCategory === 'agricultural' || isExplicitFarmPath) {
    return (
      <div className="min-h-screen bg-stone-50">
        {/* PublicAgriHub manages its internal login/registration state engine locally */}
        <PublicAgricHub />
      </div>
    );
  }

  // 🏛️ INJECT HERE: ACK DIOCESE OF NAIROBI INTEGRATED MULTI-TIER ERP GATEKEEPER
 
  if (isDiocesePath) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DiocesanRouter user={user} onLogout={logout} /> 
      </div>
    );
  }


  // 🚚 KENYAN COMMERCIAL LOGISTICS HUB GATEKEEPER (New Intercept Injection)
  if (bizCategory === 'logistics' || isLogisticsPath) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicLogisticsHub />
      </div>
    );
  }

  useEffect(() => {
    // If no candidate is selected, open the discovery modal immediately
    if (!shopId) {
      setIsDiscoveryModalOpen(true);
    }
    }, [shopId]);


  // Monitor routing and login status
  useEffect(() => {
    const path = window.location.pathname;
    const hash = window.location.hash;
    
    // DEBUG: Check exactly what the category is in the console
    if (user) {
      console.log('🩺 TeNEAR Medical Debug:', {
        username: user.username,
        category: (user as any)?.category
      });
    }
    
    if (path.startsWith('/campaign/')) {
      const id = path.split('/campaign/')[1];
      setCampaignId(id);
      setCurrentView('campaign-view');
    } else if (hash === '#qr-generation') {
      setCurrentView('qr-generation');
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [user]);

  // 1. Visitor/Public Routes
  if (currentView === 'campaign-view' && campaignId) {
    return <CampaignViewer campaignId={campaignId} />;
  }

  if (currentView === 'qr-checkin' && campaignId) {
    return (
      <QRCheckInPage 
        campaignId={campaignId}
        location={new URLSearchParams(window.location.search).get('location') || 'unknown'}
        shopId={new URLSearchParams(window.location.search).get('shop_id') || ''}
      />
    );
  }

  // 2. Main Admin Application (Protected)
  return (
    <div className="App">
      <ProtectedRoute>
        <div className="min-h-screen bg-gray-50">
          
          {/* Main Dashboard Logic: Single block to handle all 3 categories */}
          {currentView === 'dashboard' && (() => {
            const category = (user as any)?.category;
            const department = (user as any)?.department; // Ensure department is accessible

            if (category === 'medical') {
              return (
                <div className="min-h-screen">
                  <Header 
                    onBack={() => setCurrentView('dashboard')} 
                    title="TeNEAR Medical Portal" 
                    user={user} 
                    onLogout={logout} 
                    showUserMenu={showUserMenu} 
                    setShowUserMenu={setShowUserMenu} 
                  />
                  <div className="pt-16">
                    <MedicalDashboard />
                  </div>
                </div>
              );
            } 
  
            if (category === 'political') {
              return (
                <div className="min-h-screen">
                  <Header 
                    onBack={() => setCurrentView('dashboard')} 
                    title="WAJIBIKA!" 
                    user={user} 
                    onLogout={logout} 
                    showUserMenu={showUserMenu} 
                    setShowUserMenu={setShowUserMenu} 
                  />
                  <div className="pt-16">
                    {/* Added the missing prop here to fix the build error */}
                    <PoliticalDashboard onViewChange={setCurrentView} />
                  </div>
                </div>
              );
            }

            if (category === 'educational') {
              return (
                <div className="min-h-screen">
                  <Header 
                    onBack={() => setCurrentView('dashboard')} 
                    title="TeNEAR Education" 
                    user={user} 
                    onLogout={logout} 
                    showUserMenu={showUserMenu} 
                    setShowUserMenu={setShowUserMenu} 
                  />
                  <div className="pt-16">
                    {/* Create this component next */}
                    <EducationalDashboard shopId={69} /> 
                  </div>
                </div>
              );
            }

            if (category === 'church') {
              // Helper to render the correct component based on department
              const renderChurchContent = () => {
                switch (department) {
                  case 'Media':
                    return <MediaDashboard />;
                  case 'Finance':
                    return <FinanceDashboard />;
                  case 'Development':
                    // We will create this next
                    return <DevelopmentDashboard />; 
                  default:
                    return <ChurchDashboard onViewChange={(view) => setCurrentView(view)} />;
                }
              };

              // Dynamic Title Logic
              const getHeaderTitle = () => {
                if (department === 'Media') return "Church Media Center";
                if (department === 'Finance') return "Treasury & Finance";
                if (department === 'Development') return "Projects & Development";
                return "TeNEAR Church Control";
              };

              return (
                <div className="min-h-screen">
                  <Header
                    onBack={() => setCurrentView('dashboard')}
                    title={getHeaderTitle()}
                    user={user}
                    onLogout={logout}
                    showUserMenu={showUserMenu}
                    setShowUserMenu={setShowUserMenu}
                  />
                  <div className="pt-16 px-6 pb-8">
                    {/* ROUTING BY DEPARTMENT */}
                    {renderChurchContent()}
                  </div>
                </div>
              );
            }

            if (category === 'agricultural') {
              return (
                <div className="min-h-screen">
                  <Header 
                    onBack={() => setCurrentView('dashboard')} 
                    title="TeNEAR Agri-Control" 
                    user={user} 
                    onLogout={logout} 
                    showUserMenu={showUserMenu}
                    setShowUserMenu={setShowUserMenu}
                  />
                  <main className="pt-16">
                    <AgriDashboard />
                  </main>
                </div>
              );
            }

            if (category === 'ngo') {
              return (
                <div className="min-h-screen">
                  <Header 
                    onBack={() => setCurrentView('dashboard')} 
                    title="TeNEAR NGO Impact" 
                    user={user} 
                    onLogout={logout} 
                    showUserMenu={showUserMenu}
                    setShowUserMenu={setShowUserMenu}
                  />
                  <main className="pt-16">
                    <NGODashboard />
                  </main>
                </div>
              );
            }

            // Default Retail Dashboard
            return <Dashboard onViewChange={setCurrentView} />;
          })()} {/* <--- Ensure these closing symbols match exactly */}

          {currentView === 'campaigns' && (
            <div className="min-h-screen">
              <Header onBack={() => setCurrentView('dashboard')} title="Campaign Management" user={user} onLogout={logout} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} />
              <div className="pt-16"><CampaignManagement /></div>
            </div>
          )}


          {currentView === 'pos' && (
            <div className="min-h-screen">
              <Header onBack={() => setCurrentView('dashboard')} title="Point of Sale (Cash Entry)" user={user} onLogout={logout} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} />
              <div className="pt-16"><POS shopId={(user as any)?.shop_id || 0} /></div>
            </div>
          )}

          {currentView === 'templates' && (
            <div className="min-h-screen">
              <Header onBack={() => setCurrentView('dashboard')} title="Campaign Template Manager" user={user} onLogout={logout} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} />
              <div className="pt-16"><CampaignTemplateManager /></div>
            </div>
          )}

          {currentView === 'qr-generation' && (
            <div className="min-h-screen">
              <Header onBack={() => setCurrentView('dashboard')} title="QR Code Generation" user={user} onLogout={logout} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} />
              <div className="pt-16"><QRGeneration /></div>
            </div>
          )}

          {currentView === 'analytics' && (
            <div className="min-h-screen">
              <Header onBack={() => setCurrentView('dashboard')} title="Analytics & Insights" user={user} onLogout={logout} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} />
              <div className="pt-16"><Analytics /></div>
            </div>
          )}

          {currentView === 'visitor-engagement' && (
            <div className="min-h-screen">
              <Header onBack={() => setCurrentView('dashboard')} title="Visitor Engagement Manager" user={user} onLogout={logout} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} />
              <div className="pt-16"><VisitorEngagementManager user={user!} /></div>
            </div>
          )}

          {currentView === 'products' && (
            <div className="min-h-screen">
              <Header onBack={() => setCurrentView('dashboard')} title="Product Management" user={user} onLogout={logout} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} />
              <div className="pt-16">
                <ProductManager shopId={(user as any)?.shop_id || 0} onBack={() => setCurrentView('dashboard')} /> 
              </div>
            </div>
          )}

          {currentView === 'payments' && (
            <div className="min-h-screen">
              <Header onBack={() => setCurrentView('dashboard')} title="MPESA Payment Management" user={user} onLogout={logout} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} />
              <div className="pt-16"><PaymentManagement /></div>
            </div>
          )}

          {currentView === 'payment-reception' && (
            <div className="min-h-screen">
              <Header onBack={() => setCurrentView('dashboard')} title="Payment Reception" user={user} onLogout={logout} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} />
              <div className="pt-16"><ReceivingPaymentModule /></div>
            </div>
          )}

          {currentView === 'messaging' && (
            <div className="min-h-screen">
              <Header onBack={() => setCurrentView('dashboard')} title="Payment Receipt Messaging" user={user} onLogout={logout} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} />
              <div className="pt-16"><PaymentReceiptMessaging /></div>
            </div>
          )}

          {currentView === 'customer-inquiries' && (
            <div className="min-h-screen">
              <Header onBack={() => setCurrentView('dashboard')} title="Customer Inquiries" user={user} onLogout={logout} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} />
              <div className="pt-16"><CustomerInquiries /></div>
            </div>
          )}

          {currentView === 'manifestos' && (
            <div className="min-h-screen">
              <Header 
                onBack={() => setCurrentView('dashboard')} 
                title="Manifesto Builder" 
                user={user} 
                onLogout={logout} 
                showUserMenu={showUserMenu} 
                setShowUserMenu={setShowUserMenu} 
              />
              <div className="pt-16">
                <ManifestoManager />
              </div>
            </div>
          )}

          {currentView === 'diaspora-hub' && (
            <div className="min-h-screen">
              <Header 
                onBack={() => setCurrentView('dashboard')} 
                title="Diaspora Engagement Hub" 
                user={user} 
                onLogout={logout} 
                showUserMenu={showUserMenu} 
                setShowUserMenu={setShowUserMenu} 
              />
              <div className="pt-16">
                <DiasporaManager shopId={(user as any)?.shop_id?.toString() || ""} />
              </div>
            </div>
          )}

          {currentView === 'tithes' && (
            <div className="min-h-screen">
              <Header 
                onBack={() => setCurrentView('dashboard')} 
                title="Tithing & Financial Records" 
                user={user} 
                onLogout={logout} 
                showUserMenu={showUserMenu} 
                setShowUserMenu={setShowUserMenu} 
              />
            
            </div>
          )}

          {currentView === 'settings' && (
            <div className="min-h-screen">
              <Header onBack={() => setCurrentView('dashboard')} title="Settings" user={user} onLogout={logout} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} />
              <div className="pt-16"><SettingsPage /></div>
            </div>
          )}

          {/* ADD THIS AT THE END */}
          {!['campaigns', 'pos', 'templates', 'qr-generation', 'analytics', 'visitor-engagement', 'products', 'payments', 'payment-reception', 'messaging', 'customer-inquiries', 'manifestos', 'diaspora-hub', 'settings', 'dashboard'].includes(currentView) && (
            <div className="pt-20 text-center">
              <p>View "{currentView}" not found. Returning to dashboard...</p>
              <button onClick={() => setCurrentView('dashboard')} className="text-blue-600 underline">
                Go Back
              </button>
            </div>
          )}

        </div>
      </ProtectedRoute>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}
